import React from 'react';
import { string, bool } from 'prop-types';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import { Form, PrimaryButton, FieldTextInput, ImageFromFile, Icon } from '../../components';
import * as validators from '../../util/validators';
import classNames from 'classnames';
import { getMediaUrl } from '../../util/uploadedMedia';

import css from './CreatorProfileEditForm.module.css';

const ACCEPT_IMAGES = 'image/*';
const BIO_MIN_LENGTH = 50;
const BIO_MAX_LENGTH = 700;

/**
 * Creator profile edit form for the new Settings → Profile tab.
 * Mirrors OrgProfileEditForm — image, name, bio with a single Save button.
 */
const CreatorProfileEditFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        form,
        invalid,
        pristine,
        values,
        updateInProgress,
        updateProfileError,
        uploadImageInProgress,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = updateInProgress || uploadImageInProgress;
      const submitDisabled = invalid || pristine || submitInProgress;

      const nameLabel = intl.formatMessage({
        id: 'CreatorProfileEditForm.nameLabel',
      });
      const namePlaceholder = intl.formatMessage({
        id: 'CreatorProfileEditForm.namePlaceholder',
      });
      const nameRequired = validators.required(
        intl.formatMessage({
          id: 'CreatorProfileEditForm.nameRequired',
        })
      );

      const bioLabel = intl.formatMessage({
        id: 'CreatorProfileEditForm.bioLabel',
      });
      const bioPlaceholder = intl.formatMessage({
        id: 'CreatorProfileEditForm.bioPlaceholder',
      });
      const bioRequired = validators.required(
        intl.formatMessage({
          id: 'CreatorProfileEditForm.bioRequired',
        })
      );
      const bioMinLength = validators.minLength(
        intl.formatMessage(
          {
            id: 'CreatorProfileEditForm.bioMinLength',
          },
          { minLength: BIO_MIN_LENGTH }
        ),
        BIO_MIN_LENGTH
      );
      const bioMaxLength = validators.maxLength(
        intl.formatMessage(
          {
            id: 'CreatorProfileEditForm.bioMaxLength',
          },
          { maxLength: BIO_MAX_LENGTH }
        ),
        BIO_MAX_LENGTH
      );

      const profileImage = values?.profileImage;

      const imageFromFile =
        profileImage && profileImage.lastModified ? (
          <ImageFromFile
            id={profileImage.name}
            rootClassName={css.uploadedImage}
            aspectRatioClassName={css.squareAspectRatio}
            file={profileImage}
          />
        ) : null;

      const avatarComponent =
        profileImage && profileImage?.id ? (
          <div className={css.uploadedImage}>
            <div className={css.squareAspectRatio}>
              <img
                className={css.rootForImage}
                src={getMediaUrl(profileImage, 'card')}
                alt={profileImage.name || 'avatar'}
              />
            </div>
          </div>
        ) : null;

      const chooseAvatarLabel =
        imageFromFile || avatarComponent ? (
          <div className={css.avatarContainer}>
            {imageFromFile}
            {avatarComponent}
            <div className={css.changeAvatar}>
              <Icon name="edit" size={14} className={css.changeAvatarIcon} />
              <FormattedMessage id="ProfileImageForm.changeAvatar" />
            </div>
          </div>
        ) : (
          <div className={css.avatarPlaceholder}>
            <div className={css.avatarPlaceholderText}>
              <FormattedMessage id="ProfileImageForm.addYourProfilePicture" />
            </div>
          </div>
        );

      const errorMessage = updateProfileError ? (
        <p className={css.error}>
          <FormattedMessage id="CreatorProfileEditForm.saveError" />
        </p>
      ) : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.fieldList}>
            <Field
              accept={ACCEPT_IMAGES}
              id="profileImage"
              name="profileImage"
              label={chooseAvatarLabel}
              type="file"
              form={null}
              disabled={false}
            >
              {fieldProps => {
                const { accept, id, input, label, disabled } = fieldProps;
                const { name, type } = input;
                const onChange = async e => {
                  const file = e.target.files[0];
                  if (file) {
                    form.change('profileImage', file);
                    form.blur('profileImage');
                  }
                };
                return (
                  <div className={css.imageUploadWrapper}>
                    <label htmlFor={id}>{label}</label>
                    <input
                      accept={accept}
                      id={id}
                      name={name}
                      className={css.imageInput}
                      disabled={disabled}
                      onChange={onChange}
                      type={type}
                    />
                  </div>
                );
              }}
            </Field>

            <FieldTextInput
              className={css.field}
              type="text"
              id="name"
              name="name"
              label={nameLabel}
              placeholder={namePlaceholder}
              validate={nameRequired}
            />

            <FieldTextInput
              className={css.field}
              type="textarea"
              id="bio"
              name="bio"
              label={bioLabel}
              placeholder={bioPlaceholder}
              validate={validators.composeValidators(bioRequired, bioMinLength, bioMaxLength)}
            />
          </div>

          {errorMessage}

          <div className={css.footer}>
            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="CreatorProfileEditForm.save" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

CreatorProfileEditFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  updateInProgress: false,
  updateProfileError: null,
  uploadImageInProgress: false,
};

CreatorProfileEditFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  updateInProgress: bool,
  updateProfileError: propTypes.error,
  uploadImageInProgress: bool,
};

const CreatorProfileEditForm = compose(injectIntl)(CreatorProfileEditFormComponent);

export default CreatorProfileEditForm;
